import React from "react";
import { createPortal } from "react-dom";
import SvgIcon from "./SvgIcon";
import useDropdownOverlay from "../hooks/useDropdownOverlay";

/**
 *
 * options is an array like this:
 * [
    {
      text:'',
      value:...
    },
    ...
 * ]
 *
 * overlay: when true, the dropdown list is rendered in a portal with fixed
 * positioning so it appears above scroll containers and footers (e.g. modals).
 * @param {boolean} [openUpward] — when `overlay` is true, force the list above the summary (e.g. bottom toolbar).
 */
const Dropdown = ({
  summaryText,
  options,
  onSelectFn,
  fullOption,
  alignRight,
  disabled,
  overlay = false,
  openUpward = false,
  summaryStyles,
  optionStyles,
  selectedValue,
}) => {
  const { opened, toggle, close, summaryRef, listRef, listPosition } = useDropdownOverlay({
    overlay,
    alignRight: Boolean(alignRight),
    openUpward,
    remeasureKey: options,
  });

  const handleSelect = (option) => {
    onSelectFn(fullOption ? option : option.value);
    close();
  };

  const listItems = options?.map((option) => (
    <li
      key={option.value}
      onMouseDown={() => handleSelect(option)}
      style={{ ...optionStyles }}
    >
      <div style={selectedValue !== undefined ? { justifyContent: "space-between", width: "100%" } : undefined}>
        <span>{option.text || option.label}</span>
        {selectedValue !== undefined && option.value === selectedValue && (
          <SvgIcon
            src="/img/new-icons/check.svg"
            useCurrentColor
            classNames="w-5 h-5 shrink-0 text-(--success)"
          />
        )}
      </div>
    </li>
  ));

  const inlineList = (
    <ul role="listbox" className={opened ? "opened" : ""}>
      {listItems}
    </ul>
  );

  const overlayList =
    overlay && opened && listPosition
      ? createPortal(
          <ul
            ref={listRef}
            role="listbox"
            className="dropdown-overlay-list opened"
            style={{
              position: "fixed",
              ...(listPosition.top != null ? { top: listPosition.top } : {}),
              ...(listPosition.bottom != null
                ? { bottom: listPosition.bottom }
                : {}),
              ...(listPosition.left != null ? { left: listPosition.left } : {}),
              ...(listPosition.right != null
                ? { right: listPosition.right }
                : {}),
              minWidth: listPosition.minWidth,
              maxHeight: listPosition.maxHeight,
              zIndex: 10000,
            }}
          >
            {listItems}
          </ul>,
          document.body
        )
      : null;

  return (
    <details
      role="list"
      className={disabled ? "disabled" : ""}
      open
      data-align-right={alignRight ? true : undefined}
      data-overlay={overlay ? true : undefined}
    >
      <summary
        ref={summaryRef}
        onClick={(e) => {
          if (disabled || !options || options.length < 1) {
            e.preventDefault();
            return;
          }
          toggle(e);
        }}
        onBlur={(e) => {
          if (!overlay) {
            e.preventDefault();
            close();
          }
        }}
        aria-haspopup="listbox"
        style={{ ...summaryStyles }}
      >
        <p>{summaryText}</p>
        <SvgIcon
          src={opened ? "/img/svg/angle-up.svg" : "/img/svg/angle-down.svg"}
        />
      </summary>
      {overlay ? overlayList : inlineList}
    </details>
  );
};

export default Dropdown;
